import { type DeviceClass, getDeviceClass } from "./deviceClass";

/**
 * The phone gate.
 *
 * On a phone the product is the native Android/iOS app, not this web UI. A
 * phone visitor therefore gets the download screen instead of the application —
 * with one deliberate hole in the wall: the native apps authenticate through
 * **this** origin using OAuth 2.1 Authorization Code + PKCE, so sign-in,
 * sign-up, password recovery, onboarding and the account settings surface must
 * keep working on a phone or the apps cannot be signed into at all.
 *
 * The gate is applied on the route tree rather than in the shell, so a deep
 * link, a bookmark or a share-target hand-off is redirected before any
 * authenticated screen renders.
 */

/** Standalone, outside the authenticated shell so a signed-out phone reaches it. */
export const PHONE_GATE_PATH = "/uygulama";

export const PHONE_GATE_OVERRIDE_KEY = "exognitos.deviceGate.browserOverride";

/**
 * Authenticated paths that stay reachable on a phone.
 *
 * Enumerated from the real route tree in `src/app/router.tsx`. The public
 * authentication routes (`/giris`, `/kayit`, `/parola-sifirla`,
 * `/reset-password`) are children of the unauthenticated `auth` route and are
 * never gated in the first place, so they are not repeated here. The OAuth
 * authorize and consent screens live under `/oauth/` and are server-rendered
 * documents proxied to Django — the client router never sees them, so the gate
 * cannot affect them either.
 */
export const PHONE_ALLOWED_PATH_PREFIXES: readonly string[] = [
  // The download screen itself.
  PHONE_GATE_PATH,
  // The account surface: profile, password, sessions and security, connected
  // assistants, privacy. `/ayarlar` is the root a signed-in phone needs so the
  // account that an app signs into can still be managed.
  "/ayarlar",
  // Account creation is not finished until onboarding is: the session guard
  // redirects an incomplete account here, so blocking it would strand a
  // brand-new phone sign-up in a redirect loop.
  "/baslangic",
];

export function isPhoneAllowedPath(pathname: string): boolean {
  return PHONE_ALLOWED_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/**
 * Someone who explicitly asked for the browser experience keeps it.
 *
 * Stored per browser rather than on the account: it describes this device, and
 * a person who taps "continue in the browser" on a phone has not decided
 * anything about their laptop.
 */
export function hasBrowserOverride(): boolean {
  try {
    return localStorage.getItem(PHONE_GATE_OVERRIDE_KEY) === "true";
  } catch {
    // A storage-denied browser simply does not remember the choice.
    return false;
  }
}

export function setBrowserOverride(enabled: boolean): void {
  try {
    if (enabled) localStorage.setItem(PHONE_GATE_OVERRIDE_KEY, "true");
    else localStorage.removeItem(PHONE_GATE_OVERRIDE_KEY);
  } catch {
    // Not worth failing the interaction over; the choice lasts this session.
  }
}

/**
 * A return location is only ever an in-app path.
 *
 * The value travels in the URL and is handed straight to the router, so the
 * same rule the onboarding route uses applies: absolute URLs, protocol-relative
 * URLs and backslash tricks are discarded rather than followed.
 */
export function safeGateReturn(value: string | null | undefined): string | undefined {
  if (typeof value !== "string") return undefined;
  if (!value.startsWith("/") || value.startsWith("//") || value.includes("\\")) return undefined;
  return value;
}

export type PhoneGateDecision =
  | { gated: false }
  | { gated: true; to: typeof PHONE_GATE_PATH; next?: string };

export type PhoneGateInput = {
  pathname: string;
  /** The full in-app location, so "continue in the browser" can resume it. */
  href?: string;
  deviceClass?: DeviceClass;
  overridden?: boolean;
};

/** The whole decision, as data, so both the route guard and its test can read it. */
export function phoneGateDecision({
  pathname,
  href,
  deviceClass = getDeviceClass(),
  overridden = hasBrowserOverride(),
}: PhoneGateInput): PhoneGateDecision {
  if (deviceClass !== "phone") return { gated: false };
  if (overridden) return { gated: false };
  if (isPhoneAllowedPath(pathname)) return { gated: false };
  return { gated: true, to: PHONE_GATE_PATH, next: safeGateReturn(href ?? pathname) };
}
